// ./src/components/features/PollQuestions/PollQuestionsList/PollQuestionsList.tsx
//
// Event poll / quiz questions list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready. Columns:
// question, category, type (single/multi/free text), answers (option count or "open") +
// a Quiz badge. Category name + icon/color come from the shared CategoriesContext.
// Mirrors the Signup-list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { PollQuestion, pollQuestionMainImageUrl, POLL_QUESTION_TYPE, isPollChoiceType } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalPollQuestionsContext } from "@/contexts/PollQuestionsContext";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

// ============================================
// Props
// ============================================

/** A category section for the grouped list (heading + its items, already ordered). */
export interface PollQuestionGroup {
  key: string;
  label: string;
  icon?: string | null;
  color?: string | null;
  items: PollQuestion[];
}

export interface PollQuestionsListProps {
  data?: PollQuestion[];
  /**
   * Render items grouped under category headings (order = as given). When set, it
   * drives the rows; `data`/context items are ignored for layout (the flat list is
   * derived from the groups for selection + empty-state logic).
   */
  groups?: PollQuestionGroup[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditPollQuestion?: (item: PollQuestion) => void;
  onDeletePollQuestion?: (item: PollQuestion) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddPollQuestion?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** True when a search term or filter is narrowing the list — shows a "no match" message instead of the empty state. */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (a: PollQuestion): boolean => a.status === STATUSES.DISABLED;

/** A row is editable when the caller has any right on it (BE sets [] for read-only rows). */
const isEditableRow = (a: PollQuestion): boolean =>
  recordHasRight(a, "edit") || recordHasRight(a, "remove");

const TYPE_LABEL_KEYS: Record<string, { key: string; def: string }> = {
  [POLL_QUESTION_TYPE.SINGLE]: { key: "events:pollQuestions.types.single", def: "Single choice" },
  [POLL_QUESTION_TYPE.MULTI]: { key: "events:pollQuestions.types.multi", def: "Multiple choice" },
  [POLL_QUESTION_TYPE.FREE_TEXT]: { key: "events:pollQuestions.types.freeText", def: "Free text" },
};

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

const StatusBadge: React.FC<{ a: PollQuestion }> = ({ a }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(a)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const QuizBadge: React.FC<{ show: boolean }> = ({ show }) => {
  const { t } = useTranslation(["events"]);

  if (!show) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="grape">
      {t("events:pollQuestions.list.quizBadge", { defaultValue: "Quiz" })}
    </Badge>
  );
};

const CategoryCell: React.FC<{ meta: CategoryMeta | null }> = ({ meta }) => {
  if (!meta?.name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {meta.icon ? <AppIcon iconName={meta.icon} size={16} color={meta.color || undefined} /> : null}
      <Text size="md" c={meta.color || undefined} lineClamp={1}>
        {meta.name}
      </Text>
    </Group>
  );
};

const PollQuestionIcon: React.FC<{ meta: CategoryMeta | null; imageUrl?: string | null; size?: number }> = ({
  meta,
  imageUrl,
  size = 34,
}) => {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        style={{ width: size, height: size, objectFit: "cover", borderRadius: "var(--mantine-radius-md)", display: "block", flexShrink: 0 }}
      />
    );
  }

  return (
    <ThemeIcon variant="light" color={meta?.color || "grape"} radius="xl" size={size}>
      <AppIcon iconName={meta?.icon || "MessageCircle"} size={Math.round(size * 0.55)} />
    </ThemeIcon>
  );
};

const RowActions: React.FC<{
  a: PollQuestion;
  onEdit?: (a: PollQuestion) => void;
  onDelete?: (a: PollQuestion) => void;
  isDeleting: boolean;
}> = ({ a, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["events", "common"]);

  const showEdit = !!onEdit && recordHasRight(a, "edit");
  const showDelete = !!onDelete && recordHasRight(a, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(a)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(a)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  a: PollQuestion;
  name: string;
  meta: CategoryMeta | null;
  imageUrl: string | null;
  typeLabel: string;
  answers: string;
  isQuiz: boolean;
  editable: boolean;
  disabled: boolean;
  onEdit?: (a: PollQuestion) => void;
  onDelete?: (a: PollQuestion) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const PollQuestionTableRow: React.FC<RowProps> = ({
  a,
  name,
  meta,
  imageUrl,
  typeLabel,
  answers,
  isQuiz,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => a.id && onToggleSelect?.(a.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <PollQuestionIcon meta={meta} imageUrl={imageUrl} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge a={a} />
            </Group>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <CategoryCell meta={meta} />
      </Table.Td>
      <Table.Td style={{ width: 150 }}>
        <Text size="md" lineClamp={1}>
          {typeLabel}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 170 }}>
        <Group gap={6} wrap="nowrap">
          <Text size="md" lineClamp={1}>
            {answers}
          </Text>
          <QuizBadge show={isQuiz} />
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions a={a} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const PollQuestionTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <Skeleton height={14} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={14} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const PollQuestionCardComponent: React.FC<CardProps> = ({
  a,
  name,
  meta,
  imageUrl,
  typeLabel,
  answers,
  isQuiz,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const toggleSelect = () => a.id && onToggleSelect?.(a.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(a, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(a),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(a, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(a),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, a, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            mt={2}
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <PollQuestionIcon meta={meta} imageUrl={imageUrl} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge a={a} />
          </Group>
          <Box mt={4}>
            <CategoryCell meta={meta} />
          </Box>
          <Group gap={8} wrap="nowrap" mt={4} align="center">
            <Text size="sm" c="dimmed">
              {typeLabel}
            </Text>
            {answers ? <Text size="sm" c="dimmed">· {answers}</Text> : null}
            <QuizBadge show={isQuiz} />
          </Group>
        </Box>
        {showActions && <RowActions a={a} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const PollQuestionCard = React.memo(PollQuestionCardComponent);

const PollQuestionCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={['md', 'md', 'sm']} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const PollQuestionsListComponent: React.FC<PollQuestionsListProps> = ({
  data: dataProp,
  groups,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditPollQuestion,
  onDeletePollQuestion,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddPollQuestion,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditPollQuestion, onDeletePollQuestion);

  const ctx = useOptionalPollQuestionsContext();
  const categoriesContext = useOptionalCategoriesContext();

  // When grouped, the flat list (for selection, skeleton, empty-state) is derived
  // from the groups so callers pass only `groups`.
  const pollQuestions = groups ? groups.flatMap((g) => g.items) : dataProp ?? ctx?.pollQuestions ?? [];
  const loading = loadingProp ?? ctx?.loading ?? false;
  const loadingMore = loadingMoreProp ?? ctx?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? ctx?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? ctx?.loadMore;
  const isRemoving = isRemovingProp ?? ctx?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: pollQuestions.length });

  const formatType = useCallback(
    (a: PollQuestion): string => {
      const meta = a.questionType ? TYPE_LABEL_KEYS[a.questionType] : undefined;

      return meta ? t(meta.key, { defaultValue: meta.def }) : "—";
    },
    [t]
  );

  const formatAnswers = useCallback(
    (a: PollQuestion): string => {
      if (!isPollChoiceType(a.questionType)) {
        return t("events:pollQuestions.list.freeText", { defaultValue: "Open answer" });
      }

      const count = a.options?.length ?? 0;

      return t("events:pollQuestions.list.optionCount", { defaultValue: "{{count}} options", count });
    },
    [t]
  );

  const categoryMeta = useCallback(
    (a: PollQuestion): CategoryMeta | null => {
      if (!a.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(a.categoryId);

      if (!category) {
        return null;
      }

      return {
        name: category.name || category.code || null,
        icon: category.icon ?? null,
        color: category.color ?? null,
      };
    },
    [categoriesContext]
  );

  const handleEdit = useCallback(
    (a: PollQuestion) => {
      closeAllCards();
      onEditPollQuestion?.(a);
    },
    [onEditPollQuestion, closeAllCards]
  );

  const handleDelete = useCallback(
    (a: PollQuestion) => {
      closeAllCards();
      onDeletePollQuestion?.(a);
    },
    [onDeletePollQuestion, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = ctx?.isLoaded ?? (groups !== undefined || dataProp !== undefined);
  const showSkeleton = !isLoaded;

  if (!showSkeleton && pollQuestions.length === 0) {
    // A narrowed list that came back empty is a "no match", not a truly-empty event —
    // don't offer "Add item" (the fix is to clear the search/filters, not to add one).
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState
            title={t("events:pollQuestions.noMatch.title", { defaultValue: "No items match" })}
            description={t("events:pollQuestions.noMatch.description", {
              defaultValue: "Try a different search or clear the filters.",
            })}
            iconName="Search"
            size="md"
          />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("events:pollQuestions.emptyState.title", { defaultValue: "No poll questions yet" })}
          description={t("events:pollQuestions.emptyState.description", {
            defaultValue: "Ask guests something — a single or multiple-choice poll, a free-text prompt, or a scored quiz question.",
          })}
          iconName="MessageCircle"
          actionLabel={t("events:pollQuestions.emptyState.actionLabel", { defaultValue: "Add question" })}
          onAction={onAddPollQuestion}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (a: PollQuestion): boolean =>
    isSelectable &&
    ((!!onEditPollQuestion && recordHasRight(a, "edit")) ||
      (!!onDeletePollQuestion && recordHasRight(a, "remove")));

  const rowProps = (a: PollQuestion): RowProps => ({
    a,
    name: a.question || "—",
    meta: categoryMeta(a),
    imageUrl: pollQuestionMainImageUrl(a),
    typeLabel: formatType(a),
    answers: formatAnswers(a),
    isQuiz: !!a.isQuiz,
    editable: isEditableRow(a),
    disabled: isDisabled(a),
    onEdit: onEditPollQuestion ? handleEdit : undefined,
    onDelete: onDeletePollQuestion ? handleDelete : undefined,
    highlighted: a.id === highlightedId,
    isDeleting: isRemoving(a.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(a),
    selected: selection?.isSelected(a.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = pollQuestions
    .filter((a) => rowIsSelectable(a))
    .map((a) => a.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("events:pollQuestions.list.columns.question", { defaultValue: "Question" })}</Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("events:pollQuestions.list.columns.category", { defaultValue: "Category" })}
                </Table.Th>
                <Table.Th style={{ width: 150 }}>
                  {t("events:pollQuestions.list.columns.type", { defaultValue: "Type" })}
                </Table.Th>
                <Table.Th style={{ width: 170 }}>
                  {t("events:pollQuestions.list.columns.answers", { defaultValue: "Answers" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <PollQuestionTableRowSkeleton key={i} selectionColumn={isSelectable} />
                ))
              ) : groups ? (
                groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <Table.Tr>
                      <Table.Td
                        colSpan={(isSelectable ? 1 : 0) + 5}
                        style={{ background: "var(--mantine-color-default-hover)" }}
                      >
                        <Group gap="xs" wrap="nowrap">
                          {g.icon ? <AppIcon iconName={g.icon} size={16} color={g.color ?? undefined} /> : null}
                          <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                            {g.label}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                    {g.items.map((a) => (
                      <PollQuestionTableRow key={a.id} {...rowProps(a)} />
                    ))}
                  </React.Fragment>
                ))
              ) : (
                pollQuestions.map((a) => <PollQuestionTableRow key={a.id} {...rowProps(a)} />)
              )}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <PollQuestionCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  const renderCard = (a: PollQuestion) => (
    <PollQuestionCard
      {...rowProps(a)}
      forceClose={shouldForceClose(a.id || "")}
      onForceCloseComplete={() => handleForceCloseComplete(a.id || "")}
      onSwipeStateChange={(isOpen) => handleSwipeStateChange(a.id || "", isOpen)}
    />
  );

  // Grouped mobile: a heading per category, then its cards. Entrance animation is
  // skipped here (the section headings make the ordering clear without it).
  if (groups) {
    return (
      <Stack gap="lg">
        {groups.map((g) => (
          <Stack key={g.key} gap="sm">
            <Group gap="xs" wrap="nowrap" px={4}>
              {g.icon ? <AppIcon iconName={g.icon} size={16} color={g.color ?? undefined} /> : null}
              <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                {g.label}
              </Text>
            </Group>
            {g.items.map((a) => (
              <div key={a.id}>{renderCard(a)}</div>
            ))}
          </Stack>
        ))}
        {loadMore}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {pollQuestions.map((a) => (
            <motion.div
              key={a.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              {renderCard(a)}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const PollQuestionsList = React.memo(PollQuestionsListComponent);
